import React, { useState, useEffect, useCallback } from 'react';
import { api, useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import {
  Mail,
  Plus,
  X,
  Send,
  Bell,
  FileText,
  AlertTriangle,
  Clock,
  Loader2,
  CheckCircle2,
  Settings,
  Shield,
  FileCheck
} from 'lucide-react';

const RecipientList = ({ label, description, icon: Icon, iconColor, emails, onChange }) => {
  const [newEmail, setNewEmail] = useState('');

  const addEmail = () => {
    const email = newEmail.trim().toLowerCase();
    if (!email || !email.includes('@')) {
      toast.error('Email non valida');
      return;
    }
    if (emails.includes(email)) {
      toast.error('Email gia presente');
      return;
    }
    onChange([...emails, email]);
    setNewEmail('');
  };

  const removeEmail = (email) => {
    onChange(emails.filter(e => e !== email));
  };

  return (
    <div className="space-y-3">
      <div className="flex items-start gap-3">
        <div className={`w-9 h-9 ${iconColor} rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5`}>
          <Icon className="w-4 h-4" />
        </div>
        <div className="flex-1">
          <p className="font-semibold text-sm text-slate-900">{label}</p>
          <p className="text-xs text-slate-500 mt-0.5">{description}</p>
        </div>
      </div>

      <div className="flex gap-2 pl-12">
        <Input
          type="email"
          placeholder="nome@esempio.it"
          value={newEmail}
          onChange={(e) => setNewEmail(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addEmail(); }}}
          className="flex-1 h-9 text-sm"
        />
        <Button size="sm" variant="outline" onClick={addEmail} className="h-9 px-3">
          <Plus className="w-3.5 h-3.5" />
        </Button>
      </div>

      {emails.length > 0 && (
        <div className="flex flex-wrap gap-1.5 pl-12">
          {emails.map(email => (
            <Badge key={email} variant="secondary" className="bg-slate-100 text-slate-700 font-normal pl-2.5 pr-1 py-1 gap-1.5">
              {email}
              <button onClick={() => removeEmail(email)} className="hover:bg-slate-200 rounded-full p-0.5 transition-colors">
                <X className="w-3 h-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
      {emails.length === 0 && (
        <p className="text-xs text-slate-400 pl-12">Nessun destinatario configurato</p>
      )}
    </div>
  );
};

const ChannelToggleRow = ({
  title,
  description,
  pushChecked,
  emailChecked,
  onPushChange,
  onEmailChange,
  pushHidden = false,
  emailHidden = false,
  testIdPrefix,
}) => (
  <div className="flex items-start justify-between gap-4">
    <div className="flex-1 min-w-0">
      <p className="text-sm font-medium text-slate-900">{title}</p>
      <p className="text-xs text-slate-500 mt-0.5">{description}</p>
    </div>
    <div className="flex items-center gap-5 flex-shrink-0">
      {!pushHidden && (
        <div className="flex items-center gap-2">
          <span className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Push</span>
          <Switch
            checked={!!pushChecked}
            onCheckedChange={onPushChange}
            data-testid={`toggle-${testIdPrefix}-push`}
          />
        </div>
      )}
      {!emailHidden && (
        <div className="flex items-center gap-2">
          <span className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Email</span>
          <Switch
            checked={!!emailChecked}
            onCheckedChange={onEmailChange}
            data-testid={`toggle-${testIdPrefix}-email`}
          />
        </div>
      )}
    </div>
  </div>
);

const NotificationSettingsPage = () => {
  const { user } = useAuth();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  const [sendingAlert, setSendingAlert] = useState(false);

  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/notification-settings');
      setSettings(data);
    } catch {
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const saveSettings = async () => {
    try {
      setSaving(true);
      const { data } = await api.put('/notification-settings', settings);
      setSettings(data);
      toast.success('Impostazioni salvate');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const handleTestEmail = async () => {
    if (!testEmail.trim()) return;
    try {
      setSendingTest(true);
      const { data } = await api.post('/notification-settings/test-email', { email: testEmail });
      toast.success(data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore invio test');
    } finally {
      setSendingTest(false);
    }
  };

  const handleSendContractAlert = async () => {
    try {
      setSendingAlert(true);
      const { data } = await api.post('/notifications/send-contract-alert');
      toast.success(data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore invio alert');
    } finally {
      setSendingAlert(false);
    }
  };

  const updateField = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-900" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-3xl" data-testid="notification-settings-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Impostazioni Notifiche</h1>
        <p className="text-slate-500 mt-1 text-sm">Configura i destinatari per report, alert e notifiche email</p>
      </div>

      {/* SMTP Status */}
      <Card className="border-slate-200/80 shadow-sm">
        <CardContent className="p-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${settings?.smtp_configured ? 'bg-green-100' : 'bg-amber-100'}`}>
                <Mail className={`w-5 h-5 ${settings?.smtp_configured ? 'text-green-700' : 'text-amber-700'}`} />
              </div>
              <div>
                <p className="font-semibold text-sm text-slate-900">Server SMTP</p>
                <p className="text-xs text-slate-500">
                  {settings?.smtp_configured
                    ? 'Configurato e pronto per inviare email'
                    : 'Non configurato — Compila le credenziali SMTP nel server (.env)'}
                </p>
              </div>
            </div>
            <Badge className={settings?.smtp_configured ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>
              {settings?.smtp_configured ? 'Attivo' : 'Da Configurare'}
            </Badge>
          </div>

          {/* Test email */}
          <div className="flex gap-2 mt-4 pt-4 border-t border-slate-100">
            <Input
              type="email"
              placeholder="Invia email di test a..."
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              className="flex-1 h-9 text-sm"
              data-testid="test-email-input"
            />
            <Button
              size="sm"
              variant="outline"
              onClick={handleTestEmail}
              disabled={sendingTest || !testEmail.trim() || !settings?.smtp_configured}
              className="h-9"
              data-testid="send-test-email"
            >
              {sendingTest ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Send className="w-3.5 h-3.5 mr-1.5" />}
              Test
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Recipients Configuration */}
      <Card className="border-slate-200/80 shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-bold flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <Bell className="w-4 h-4 text-slate-600" />
            Destinatari Notifiche
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6 pt-4">

          <RecipientList
            label="Report Periodici"
            description="Ricevono il report settimanale/mensile con KPI e statistiche"
            icon={FileText}
            iconColor="bg-blue-100 text-blue-700"
            emails={settings?.report_recipients || []}
            onChange={(v) => updateField('report_recipients', v)}
          />

          <Separator />

          <RecipientList
            label="Alert Manutenzioni Contrattuali"
            description="Ricevono notifica quando una manutenzione da fornitore esterno e in scadenza o scaduta"
            icon={FileCheck}
            iconColor="bg-amber-100 text-amber-700"
            emails={settings?.contract_recipients || []}
            onChange={(v) => updateField('contract_recipients', v)}
          />

          <Separator />

          <RecipientList
            label="Ticket Urgenti"
            description="Ricevono notifica immediata quando viene creato un ticket con priorita urgente"
            icon={AlertTriangle}
            iconColor="bg-red-100 text-red-700"
            emails={settings?.urgent_ticket_recipients || []}
            onChange={(v) => updateField('urgent_ticket_recipients', v)}
          />

          <Separator />

          <RecipientList
            label="Violazioni SLA"
            description="Ricevono notifica quando un ticket supera le 24h o 48h senza risoluzione"
            icon={Clock}
            iconColor="bg-purple-100 text-purple-700"
            emails={settings?.sla_breach_recipients || []}
            onChange={(v) => updateField('sla_breach_recipients', v)}
          />
        </CardContent>
      </Card>

      {/* Toggle switches — Push + Email per ogni evento */}
      <Card className="border-slate-200/80 shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-bold flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <Settings className="w-4 h-4 text-slate-600" />
            Canali di Notifica per Evento
          </CardTitle>
          <p className="text-xs text-slate-500 mt-1">
            Ogni evento ha due canali indipendenti: push (in-app / browser) ed email. Puoi tenere il push acceso e spegnere solo la mail.
          </p>
        </CardHeader>
        <CardContent className="space-y-5 pt-4">
          <ChannelToggleRow
            title="Ticket Urgenti / Alta Priorita"
            description="Nuovo ticket con priorita urgente o alta"
            pushChecked={settings?.urgent_ticket_push_enabled ?? true}
            emailChecked={settings?.urgent_ticket_email_enabled ?? true}
            onPushChange={(v) => updateField('urgent_ticket_push_enabled', v)}
            onEmailChange={(v) => updateField('urgent_ticket_email_enabled', v)}
            testIdPrefix="urgent-ticket"
          />
          <Separator />
          <ChannelToggleRow
            title="Nuovo Ticket (priorita normale)"
            description="Ticket standard (media/bassa priorita) — solo push per non intasare la mail"
            pushChecked={settings?.new_ticket_push_enabled ?? true}
            onPushChange={(v) => updateField('new_ticket_push_enabled', v)}
            emailHidden
            testIdPrefix="new-ticket"
          />
          <Separator />
          <ChannelToggleRow
            title="Ticket Assegnato"
            description="Notifica al tecnico quando gli viene assegnato un ticket"
            pushChecked={settings?.ticket_assigned_push_enabled ?? true}
            emailChecked={settings?.ticket_assigned_email_enabled ?? true}
            onPushChange={(v) => updateField('ticket_assigned_push_enabled', v)}
            onEmailChange={(v) => updateField('ticket_assigned_email_enabled', v)}
            testIdPrefix="ticket-assigned"
          />
          <Separator />
          <ChannelToggleRow
            title="Cambio Stato Ticket"
            description="Push agli utenti dell'hotel quando lo stato di un ticket cambia"
            pushChecked={settings?.ticket_update_push_enabled ?? true}
            onPushChange={(v) => updateField('ticket_update_push_enabled', v)}
            emailHidden
            testIdPrefix="ticket-update"
          />
          <Separator />
          <ChannelToggleRow
            title="Manutenzioni Contrattuali"
            description="Alert per scadenze contrattuali imminenti"
            emailChecked={settings?.contract_email_enabled ?? true}
            onEmailChange={(v) => updateField('contract_email_enabled', v)}
            pushHidden
            testIdPrefix="contract"
          />
          <Separator />
          <ChannelToggleRow
            title="Violazioni SLA"
            description="Alert quando un ticket supera l'SLA (24h/48h)"
            emailChecked={settings?.sla_breach_email_enabled ?? true}
            onEmailChange={(v) => updateField('sla_breach_email_enabled', v)}
            pushHidden
            testIdPrefix="sla-breach"
          />
        </CardContent>
      </Card>

      {/* Report schedule */}
      <Card className="border-slate-200/80 shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-bold flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <FileText className="w-4 h-4 text-slate-600" />
            Pianificazione Report
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-slate-500">Frequenza</label>
              <Select
                value={settings?.report_frequency || 'settimanale'}
                onValueChange={(v) => updateField('report_frequency', v)}
              >
                <SelectTrigger data-testid="report-frequency">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="settimanale">Settimanale</SelectItem>
                  <SelectItem value="mensile">Mensile</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-slate-500">Giorno invio</label>
              <Select
                value={settings?.report_day || 'lunedi'}
                onValueChange={(v) => updateField('report_day', v)}
              >
                <SelectTrigger data-testid="report-day">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="lunedi">Lunedi</SelectItem>
                  <SelectItem value="martedi">Martedi</SelectItem>
                  <SelectItem value="mercoledi">Mercoledi</SelectItem>
                  <SelectItem value="giovedi">Giovedi</SelectItem>
                  <SelectItem value="venerdi">Venerdi</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-slate-500">Ora invio</label>
              <Input
                type="time"
                value={settings?.report_time || '08:00'}
                onChange={(e) => updateField('report_time', e.target.value)}
                data-testid="report-time"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex items-center justify-between pt-2">
        <Button
          variant="outline"
          onClick={handleSendContractAlert}
          disabled={sendingAlert || !settings?.smtp_configured || !(settings?.contract_recipients?.length > 0)}
          data-testid="send-contract-alert-button"
        >
          {sendingAlert ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <FileCheck className="w-4 h-4 mr-2" />}
          Invia Alert Contratti Ora
        </Button>

        <Button
          className="bg-[#1e3a5f] hover:bg-[#243b53]"
          onClick={saveSettings}
          disabled={saving}
          data-testid="save-settings-button"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
          Salva Impostazioni
        </Button>
      </div>
    </div>
  );
};

export default NotificationSettingsPage;
